package com.siriusxm.pia.views.unifiedaggregator.events

import androidx.compose.runtime.*
import com.siriusxm.pia.components.PeriodType
import com.siriusxm.pia.components.TimeRange
import com.siriusxm.pia.components.box
import com.siriusxm.pia.components.boxMessage
import com.siriusxm.pia.rest.unifiedaggregator.CloudEventChanges
import com.siriusxm.pia.rest.unifiedaggregator.diffEvents
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import unifiedcontent.aggregator.CloudEventList
import unifiedcontent.aggregator.EventQueryOption
import unifiedcontent.aggregator.EventSearchStatus
import unifiedcontent.aggregator.EventSearchStatusState
import io.ktor.http.*
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Duration.Companion.minutes

val defaultTimeRanges = listOf(
    PeriodType.Hours.periodOf(12),
    PeriodType.Days.periodOf(1),
    PeriodType.Days.periodOf(3),
    PeriodType.Weeks.periodOf(1),
    PeriodType.Weeks.periodOf(2),
    PeriodType.Months.periodOf(1),
    PeriodType.Months.periodOf(3)
)

/**
 * Displays events for an entity.
 *
 * **Controlled**, same shape as [ProducerValidEvents] (UCA-1028)/(UCA-1039): holds no filter state
 * of its own, and every filter change is handed to [onFiltersChange]. That is what lets the entity
 * detail page keep the selections in the URL.
 *
 * @param filters the current filter selections. [EventFilters.range] and [EventFilters.queryType]
 *   both drive the server query; [EventFilters.eventType] filters the loaded events client-side.
 * @param onFiltersChange called with the new selections whenever the user changes a filter.
 */
@Composable
fun entityEvents(
    aggregatorContext: AggregatorService,
    entityId: String,
    filters: EventFilters,
    onFiltersChange: (EventFilters) -> Unit
) {
    var loading by remember { mutableStateOf(false) }
    var result by remember { mutableStateOf<EventSearchStatus?>(null) }
    var events by remember { mutableStateOf<CloudEventList?>(null) }
    var failureMessage by remember { mutableStateOf<String?>(null) }

    var cloudEventChangeMap by remember { mutableStateOf<Map<String, CloudEventChanges>?>(null) }

    val eventsPeriod = filters.range
    val queryType = filters.queryType
    val selectedEventType = filters.eventType

    Style(EntityEventsStyles)

    LaunchedEffect(entityId, eventsPeriod, queryType) {
        val range = eventsPeriod?.range()
        val selectedQuery = queryType
        if (range == null || selectedQuery == null) {
            loading = false
            result = null
            events = null
            failureMessage = null
            cloudEventChangeMap = null
            return@LaunchedEffect
        }
        if (eventsPeriod?.isOutsideEventRetention() == true) {
            // A range from a link or bookmark can predate retention; don't send a search the
            // backend can only reject (UCA-1168).
            loading = false
            result = null
            events = null
            cloudEventChangeMap = null
            failureMessage = EVENT_RETENTION_MESSAGE
            return@LaunchedEffect
        }

        failureMessage = null
        loading = true
        result = null
        events = null
        cloudEventChangeMap = null
        try {
            result = loadAccumulatedEvents({ queryId ->
                if (queryId != null) {
                    aggregatorContext.clientApi.getEntityEvents(entityId, queryId = queryId)
                } else {
                    aggregatorContext.clientApi.getEntityEvents(
                        entityId, start = range.start,
                        end = range.endInclusive, options = listOf(selectedQuery)
                    )
                }
            }) {
                events = it
            }
            if (result?.state == EventSearchStatusState.FAILED) {
                failureMessage = "The event search failed for an unknown reason."
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            failureMessage = e.message ?: "The event search failed for an unknown reason."
        } finally {
            loading = false
        }
    }

    // Updates the changes map based on results.
    LaunchedEffect(result, events) {
        if (result?.state == EventSearchStatusState.COMPLETED) {
            val cloudEventChangesMap = mutableMapOf<String, CloudEventChanges>()
            val publishedCloudEvents = events.orEmpty()
            publishedCloudEvents.windowed(size = 2, step = 1) { it[0] to it[1] }.forEach {
                val cloudEventChanges = diffEvents(it.first, it.second)
                if (cloudEventChanges.changedKeys.isNotEmpty()) {
                    cloudEventChangesMap[it.first.id] = cloudEventChanges
                }
            }
            cloudEventChangeMap = cloudEventChangesMap
        }
    }


    eventsSelectionHeader(
        loading, selectedTimeRange = eventsPeriod, selectedQueryOption = queryType,
        showQueryOptions = true,
        selectedEventType = selectedEventType,
        showEventTypeFilter = true
    ) { range, option, eventType ->
        onFiltersChange(
            filters.copy(
                range = range,
                queryType = option,
                eventType = if (option == null) null else eventType
            )
        )
    }

    box({
        paddedContent = false
    }) {
        if (failureMessage != null) {
            boxMessage(failureMessage!!)
        } else if (eventsPeriod == null) {
            boxMessage("Please select a time period.")
        } else if (queryType == null) {
            boxMessage("Select Outgoing or Incoming events to view results.")
        }
        if (eventsPeriod != null && queryType != null && failureMessage == null) {
            val resultEvents = events.orEmpty()
            val filteredResults = resultEvents.filterByEventTypes(selectedEventType.asFilterSet())
            if (filteredResults.isNotEmpty()) {
                val producerMap = aggregatorContext.producers.associateBy { it.id }

                cloudEventTable(
                    aggregatorContext,
                    filteredResults,
                    showPreciseTimestamp = true,
                    rendererFactory = { event ->
                        // a custom lookup to create a renderer with a changes record.
                        val changes = cloudEventChangeMap?.get(event.id)
                        if (changes != null && queryType == EventQueryOption.Output) {
                            AtlasEventRenderer(aggregatorContext, event, changes)
                        } else {
                            defaultCloudEventRendererLookup(aggregatorContext, event)
                        }
                    },
                    extraConfig = if (queryType == EventQueryOption.Output) {
                        {
                            column("Sequence") {
                                content { event ->
                                    event.sequence?.let { Text(it.trimStart('0').ifEmpty { "0" }) }
                                }
                            }
                            column("Changes") {
                                content { event ->
                                    val changes = cloudEventChangeMap?.get(event.id)
                                    if (changes != null) {
                                        Text(changes.changedKeys.joinToString(", "))
                                    }
                                }
                            }
                        }
                    } else if (queryType == EventQueryOption.Incoming) {
                        {
                            column("Producer") {
                                content { event ->
                                    val source = event.source
                                    val name = producerMap.get(event.source)?.name ?: source
                                    Text(name)
                                }
                            }
                            column("Region") {
                                content { event ->
                                    val sourceRegion = event.sourceregion
                                    if (sourceRegion != null) {
                                        Text(sourceRegion)
                                    }
                                }
                            }
                            column("Trace") {
                                content { event ->
                                    val stage = aggregatorContext.context.configuration.stage
                                    val startTs = event.time.minus(5.minutes)
                                    val endTs = event.time.plus(20.minutes)
                                    val dataDogUrl = buildUrl {
                                        takeFrom("https://app.datadoghq.com/logs")

                                        parameters.append(
                                            "query", """
                                            service:unified-content-aggregator deploymentid:${stage} (@trace_id:${event.id} OR @detail.tracingcontext.trace_id:${event.id})
                                        """.trimIndent()
                                        )

                                        parameters.append("to_ts", endTs.toEpochMilliseconds().toString())
                                        parameters.append("from_ts", startTs.toEpochMilliseconds().toString())
                                        parameters.append("storage", "flex_tier")
                                        parameters.append("fromUser", "true")
                                        parameters.append("live", "false")
                                        parameters.append(
                                            "cols", listOf(
                                                "region",
                                                "shortfunctionname",
                                                "sxm_uni-content_subsystem"
                                            ).joinToString(",")
                                        )
                                    }
                                    A(href = dataDogUrl.toString(), {
                                        target(ATarget.Blank)
                                    }) {
                                        Text("trace")
                                    }
                                }
                            }
                        }
                    } else null
                )
            } else if (resultEvents.isNotEmpty() && selectedEventType != null) {
                boxMessage("No events match the selected event types.")
            } else if (resultEvents.isEmpty() && !loading) {
                boxMessage("No events occurred in this period.")
            }
        }
    }
}
